<template>
  <article v-if="!readOnly || documents.length">
    <div class="container-fluid">
      <div class="row">
        <div class="col px-0">
          <SectionTitle has-line :title="'Documentos'" />
        </div>
      </div>
    </div>
    <div v-if="!showDocumentForm" class="container-fluid">
      <slot name="default-documents" />
    </div>
    <div
      v-if="!showDocumentForm && !documents.length"
      class="row justify-content-center align-items-center"
    >
      <div class="col-12">
        <ActionButton
          v-if="!readOnly"
          type="activationbutton"
          :class="{ 'pt-4': infoMessage }"
          :text="'Presione aquí para anejar documento'"
          @click="toggleShowDocumentForm"
        />
        <div
          v-if="infoMessage"
          class="bg-light-gray border-radius--light col-12 pb-2 text-muted font-size-info-message"
        >
          <em class="far fa-exclamation-circle" />
          {{ infoMessage }}
        </div>
      </div>
    </div>
    <template v-else>
      <ValidationObserver v-if="showDocumentForm" ref="documentsForm">
        <div class="container-fluid mb-3">
          <div class="row box-container">
            <div class="col-12 col-md-3 mb-4 mb-md-0 pt-2">
              <ValidationProvider v-slot="{ errors }" rules="required">
                <custom-drop-down
                  v-model="documentType"
                  :options="documentTypes"
                  fieldkey="id"
                  fieldname="name"
                  label="Tipo de Documento:"
                  placeholder="Seleccione"
                  :error="errors[0]"
                  custom-error-msg="Favor seleccionar el tipo de documento."
                />
              </ValidationProvider>
            </div>
            <div class="col-12 col-md-5 mb-4 mb-md-0 pt-2">
              <label class="custom-file-input-label">Archivo:</label>
              <ValidationProvider v-slot="{ errors }" rules="required">
                <CustomFileInput
                  ref="fileUpload"
                  v-model="documentFile"
                  display-style="normal"
                  :on-download="() => {}"
                  :accept="'.pdf'"
                  label-position="left"
                  :error="errors[0]"
                  custom-error-msg="Favor seleccionar un documento."
                />
              </ValidationProvider>
            </div>
            <div
              class="col-12 pt-4 mt-2 pt-md-4 col-md-2 d-flex align-self-center justify-content-end"
              :class="isFormValid ? 'align-items-end' : 'align-items-center'"
            >
              <ActionButton
                class="max-width-sm"
                variant="outline-danger m-1"
                i="fas fa-times-circle"
                text="Cancelar"
                :responsive="true"
                @click="toggleShowDocumentForm()"
              />
            </div>
            <div
              class="col-12 pt-4 mt-2 pt-md-4 col-md-2 d-flex align-self-center justify-content-end"
              :class="isFormValid ? 'align-items-end' : 'align-items-center'"
            >
              <ActionButton
                variant="success m-1"
                i="fas fa-save"
                text="Guardar"
                :responsive="true"
                @click="onAddDocument()"
              />
            </div>
          </div>
        </div>
      </ValidationObserver>
      <div
        v-if="(!showDocumentForm && documents.length) || documents.length"
        class="container-fluid"
      >
        <div
          v-for="item in documents"
          :key="item.documentId"
          class="row box-container mb-3"
        >
          <div class="col-6 col-md-5">
            <div class="row">
              <div class="col-12">
                <span class="font-weight-normal">Tipo de Documento:</span>
              </div>
              <div class="col-12">
                <span class="font-weight-regular">
                  {{ item.documentTypeName }}
                </span>
              </div>
            </div>
          </div>

          <div class="col-6 col-md-5">
            <div class="row">
              <div class="col-12">
                <span class="font-weight-normal">Archivo:</span>
              </div>
              <div class="col-12">
                <span class="font-weight-regular">
                  {{ item.filename }}
                </span>
              </div>
            </div>
          </div>

          <div class="col-12 d-block d-md-none py-2" />

          <div class="col-12 col-md-2 pt-2">
            <div
              class="h-100 d-md-flex d-lg-flex justify-content-end align-items-center"
            >
              <drop-down :items="getDocumentActions(item)" class="d-sm-block" />
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-12 d-flex justify-content-end align-items-end pr-0">
            <ActionButton
              v-if="!readOnly"
              class="max-width-sm"
              variant="success"
              i="fas fa-file-plus"
              @click="toggleShowDocumentForm()"
            >
              Añadir
            </ActionButton>
          </div>
        </div>
      </div>
    </template>
    <error
      v-if="showDocumentsError"
      message="Seleccione el documento de querella."
      variant="no-border"
    />
  </article>
</template>

<script>
import SectionTitle from 'SectionTitle';
import ActionButton from 'ActionButton';
import CustomDropDown from 'custom-drop-down';
import CustomFileInput from 'CustomFileInput';
import documentsService from '@/services/documents';
import materialsService from '@/services/api/materialsEquipment.api.js';
import objectToFormData from 'object-to-formdata';
import {
  uploadDocument,
  deleteEvidenceDocument,
} from '@/modules/materials-equipment/api.js';
import alert from '@/mixin/alert.mixin';
import DropDown from 'dropdown';
import Error from 'custom-error';

export default {
  name: 'Document',
  components: {
    SectionTitle,
    ActionButton,
    CustomDropDown,
    CustomFileInput,
    DropDown,
    Error,
  },
  mixins: [alert],
  props: {
    documents: {
      type: Array,
      required: false,
      default: () => [],
      validator: (value) => Array.isArray(value),
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    infoMessage: {
      type: String,
      default: null,
    },
    id: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      showDocumentForm: false,
      showDocumentsError: false,
      documentType: null,
      documentFile: null,
      isFormValid: true,
      documentTypes: [],
    };
  },
  computed: {
    getDocumentActions() {
      const self = this;
      return (document) => [
        {
          label: 'Descargar',
          icon: 'fas fa-file-download text-green pr-1',
          click: () => this.downloadFile(document),
        },
        {
          label: 'Eliminar',
          icon: 'fas fa-times-circle text-red pr-1',
          visible: !self.readOnly,
          click: () => this.deleteDocument(document),
        },
      ];
    },
  },
  mounted() {
    this.fetchDocumentTypes();
  },
  methods: {
    async fetchDocumentTypes() {
      const response = await materialsService.getDocumentTypes();
      this.documentTypes = response.data;
    },
    toggleShowDocumentForm() {
      if (this.readOnly) return;

      this.isFormValid = true;
      this.showDocumentForm = !this.showDocumentForm;
    },
    async onAddDocument() {
      const isValid = await this.$refs.documentsForm.validate();

      if (!isValid) {
        this.isFormValid = isValid;
        return;
      }

      const document = this.documentType;
      const file = {
        documentTypeId: document.id,
        documentTypeName: document.name,
        filename: this.documentFile.name,
        file: this.documentFile,
      };
      await this.saveDocument(file);
      this.documents.push(file);
      this.$refs.fileUpload.removeFile();
      this.documentType = null;
      this.documentFile = null;
      this.$refs.documentsForm.reset();
      this.toggleShowDocumentForm();
      this.$emit('update-documents');
    },
    downloadFile(document) {
      if (document.documentId) {
        documentsService.downloadFromServer(document.documentId);
      } else {
        let file = {
          filename: document.filename,
          content: document.file,
          contentType: 'application/pdf',
        };
        documentsService.downloadFile(file);
      }
    },

    async saveDocument(document) {
      const doc = objectToFormData({
        Id: this.id,
        DocumentTypeId: document.documentTypeId,
        File: document.file,
        FileName: document.filename,
        Date: new Date(),
      });
      this.showDocumentsError = false;
      await uploadDocument(doc).then(() => {
        this.ShowToast(
          'Confirmación',
          'El documento ha sido guardado exitosamente',
          'success'
        );
      });
    },
    async deleteDocument(document) {
      let response = await this.ShowCancelModal(
        'Atención',
        `¿Está seguro que desea eliminar el documento ${document.filename}?`,
        'Aceptar',
        'Cancelar'
      );
      if (!response) return;
      await deleteEvidenceDocument(document.documentId)
        .then(() => {
          this.ShowToast(
            'Confirmación',
            'El documento fue eliminado exitosamente.',
            'success'
          );
          this.$emit('update-documents');
        })
        .catch((err) => {
          if (err && err.response && err.response.data) {
            this.ShowToast('Error', err.response.data.message, 'error');
          } else {
            this.ShowToast('Error', err.message, 'error');
          }
        });
    },
    showRequiredMsg() {
      this.showDocumentsError = true;
    },
  },
};
</script>

<style scoped>
.box-container {
  background-color: #fafafa;
  padding: 10px 0 10px 0;
  border-radius: 5px;
}
.custom-file-input-label {
  margin-bottom: 8px;
  color: #727272;
}
.font-size-info-message {
  font-size: 0.875rem;
}
@media (max-width: 765px) {
  .max-width-sm {
    width: 100%;
  }
}
</style>
